"use client";

import { motion } from "framer-motion";
import { BarChart3, Info, ShieldCheck, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";

import { LegalDialog } from "@/components/legal-dialogs";
import { HealthBanner } from "@/components/system/health-banner";

import { LoginForm } from "./login-form";

export function LoginClient({ hasPasskeys = false }: { hasPasskeys?: boolean }) {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    return (
        <main className="min-h-screen relative overflow-y-auto overflow-x-hidden bg-[#0a0e1a] text-white">
            {/* Enterprise SaaS Background System */}
            <div className="zp-login-bg">
                <div className="zp-login-glow" />
                <div className="zp-login-noise" />
                <div className="zp-login-vignette" />
            </div>

            <div className="relative min-h-screen flex flex-col items-center justify-start lg:justify-center p-4 lg:p-6 pt-1 sm:pt-6 lg:pt-0">
                <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 lg:items-stretch items-center">
                    {/* Left Column: Marketing Content */}
                    <div className="hidden lg:flex flex-col justify-center pl-8 lg:pl-12">
                        <motion.div
                            initial={false}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                            className="max-w-lg space-y-8"
                        >
                            {/* Main Heading */}
                            <div className="space-y-4">
                                <h1 className="text-3xl xl:text-4xl font-bold leading-[1.1] tracking-tight text-white">
                                    Intelligenz für Ihre<br />
                                    Infrastruktur
                                </h1>
                                <p className="text-base text-slate-300 leading-relaxed max-w-md">
                                    Verbrauchsdaten, Analysen und Berichte – sicher & zentral.
                                </p>
                            </div>

                            {/* Feature Cards - Clean, no excessive effects */}
                            <div className="space-y-3">
                                {[
                                    {
                                        title: "Live-Monitoring",
                                        desc: "Echtzeit-Überblick aller Zähler",
                                        icon: TrendingUp,
                                        iconBg: "bg-blue-600/10 text-blue-500"
                                    },
                                    {
                                        title: "Präzise Analysen",
                                        desc: "Detaillierte Statistiken & Trends",
                                        icon: BarChart3,
                                        iconBg: "bg-indigo-600/10 text-indigo-500"
                                    },
                                    {
                                        title: "Sicherheit",
                                        desc: "DSGVO-konforme Datensicherheit",
                                        icon: ShieldCheck,
                                        iconBg: "bg-teal-600/10 text-teal-500"
                                    }
                                ].map((item, i) => (
                                    <motion.div
                                        key={i}
                                        initial={false}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.3 + (i * 0.1), duration: 0.5 }}
                                        className="flex items-center gap-3 p-3 rounded-xl bg-slate-900/40 border border-slate-800/50 backdrop-blur-sm w-full shadow-sm"
                                    >
                                        <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${item.iconBg} flex items-center justify-center border border-white/5`}>
                                            <item.icon className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-white">{item.title}</h3>
                                            <p className="text-xs text-slate-300">{item.desc}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Legal Info Box - Professional, clean */}
                            <motion.div
                                initial={false}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.7, duration: 0.5 }}
                                className="p-3 rounded-xl bg-slate-800/30 border border-slate-700/40"
                            >
                                <div className="flex gap-3">
                                    <Info className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-slate-300 leading-relaxed">
                                        <span className="text-white font-medium">Ihr Recht auf Ihre Verbrauchsdaten:</span><br />
                                        Gemäß der{" "}
                                        <a href="https://eur-lex.europa.eu/legal-content/DE/TXT/PDF/?uri=CELEX:32023L1791" target="_blank" rel="noopener noreferrer" className="text-slate-300 underline underline-offset-2 hover:text-white transition-colors">
                                            EU-Energieeffizienz-Richtlinie (EED)
                                        </a>{" "}
                                        und dem{" "}
                                        <a href="https://www.ris.bka.gv.at/NormDokument.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10007277&Paragraf=17" target="_blank" rel="noopener noreferrer" className="text-slate-300 underline underline-offset-2 hover:text-white transition-colors">
                                            HeizKG
                                        </a>{" "}
                                        haben Sie als Endverbraucher das Recht, Ihre Verbrauchsdaten jederzeit einzusehen.
                                    </p>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>

                    {/* Right Column: Login Card - Clean, professional */}
                    <div className="flex flex-col items-center justify-center relative z-10 w-full min-h-full pt-0 lg:pt-0">
                        <motion.div
                            initial={false}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
                            className="w-full max-w-md"
                        >
                            <HealthBanner className="mx-0 mt-0 mb-4" />
                            <LoginForm hasPasskeys={hasPasskeys} />
                        </motion.div>

                        {/* Mobile Only: EED/HeizKG Hint (Visible on mobile, hidden on desktop) */}
                        <div className="mt-12 lg:hidden max-w-md w-full">
                            <div className="p-3 rounded-xl bg-slate-900/50 border border-slate-800/50">
                                <div className="flex gap-3">
                                    <Info className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-slate-300 leading-relaxed">
                                        <span className="text-white font-medium">Ihr Recht auf Ihre Verbrauchsdaten:</span><br />
                                        Gemäß der{" "}
                                        <a href="https://eur-lex.europa.eu/legal-content/DE/TXT/PDF/?uri=CELEX:32023L1791" target="_blank" rel="noopener noreferrer" className="text-slate-300 underline underline-offset-2 hover:text-white transition-colors">
                                            EU-Energieeffizienz-Richtlinie (EED)
                                        </a>{" "}
                                        und dem{" "}
                                        <a href="https://www.ris.bka.gv.at/NormDokument.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10007277&Paragraf=17" target="_blank" rel="noopener noreferrer" className="text-slate-300 underline underline-offset-2 hover:text-white transition-colors">
                                            HeizKG
                                        </a>{" "}
                                        haben Sie als Endverbraucher das Recht, Ihre Verbrauchsdaten jederzeit einzusehen.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Legal Links Footer - Moved from LoginForm, pushed down on mobile */}
                        <div className="mt-12 lg:mt-8 pb-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-slate-500 font-medium">
                            {mounted ? (
                                <>
                                    <LegalDialog type="imprint">
                                        <button type="button" className="hover:text-slate-300 cursor-pointer transition-colors">Impressum</button>
                                    </LegalDialog>
                                    <LegalDialog type="privacy">
                                        <button type="button" className="hover:text-slate-300 cursor-pointer transition-colors">Datenschutz</button>
                                    </LegalDialog>
                                    <LegalDialog type="terms">
                                        <button type="button" className="hover:text-slate-300 cursor-pointer transition-colors">Nutzungsbedingungen</button>
                                    </LegalDialog>
                                </>
                            ) : (
                                <>
                                    <span className="cursor-default">Impressum</span>
                                    <span className="cursor-default">Datenschutz</span>
                                    <span className="cursor-default">Nutzungsbedingungen</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
